import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { 
  Clock, Award, TrendingUp, CheckCircle, Globe, Shield, Users, 
  Target, Share2, BarChart, Zap, Mic, Radio, Headphones, UserCheck, 
  Layers, MessageSquare, Settings, DollarSign, Truck, Palette, BookOpen, 
  RefreshCw, FileText, Package, Sliders, Phone, ArrowRight, Star, Check, MapPin
} from "lucide-react";
import { services, testimonials, portfolioProjects, companyInfo } from "../mock";
import SEO from "../components/SEO";
import ServicePopup from "../components/ServicePopup";
import { openChat } from "../utils/chatUtils";
import countryCodes from "../utils/countryCodes";
import { useFormSubmit } from "../hooks/useFormSubmit";
import AnimatedIcon from "../components/AnimatedIcon";
import AOS from "aos";
import "aos/dist/aos.css";

const iconMap = {
  Clock, Award, TrendingUp, CheckCircle, Globe, Shield, Users, 
  Target, Share2, BarChart, Zap, Mic, Radio, Headphones, UserCheck, 
  Layers, MessageSquare, Settings, DollarSign, Truck, Palette, BookOpen, 
  RefreshCw, FileText, Package, Sliders
};

const ServicePage = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);
  
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "+1",
    message: ""
  });

  // Use centralized form submission hook
  const { isSubmitting, formStatus, handleSubmit, geoData, isLoadingGeo } = useFormSubmit();

  const [activeGenre, setActiveGenre] = useState("Children\'s Book");
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupService, setPopupService] = useState({ title: "", description: "" });

  const openServicePopup = (title, description) => {
    setPopupService({ title, description });
    setPopupOpen(true);
  };

  // Auto-select country based on geolocation
  useEffect(() => {
    if (geoData?.isSuccess && geoData?.data?.countryCode) {
      const matchingCountry = countryCodes.find(
        country => country.country === geoData.data.countryName
      );
      
      if (matchingCountry) {
        setContactForm(prev => ({
          ...prev,
          countryCode: matchingCountry.code
        }));
      }
    }
  }, [geoData]);

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-out",
      once: true,
      offset: 100
    });

    window.scrollTo(0, 0);
    // Parallax effect for hero background
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const heroBackground = document.querySelector(".hero-background");
      if (heroBackground) {
        heroBackground.style.transform = `translateY($\{scrolled * 0.5}px)`;
      }
    };

    window.addEventListener("scroll", handleScroll);
    
    
    // Testimonials auto-slide
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prevIndex) => {
        const maxIndex = Math.ceil(testimonials.length / 3) - 1;
        return prevIndex === maxIndex ? 0 : prevIndex + 1;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#F4D03F] mb-4">Service Not Found</h1>
          <Link to="/">
            <Button>Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const getButtonText = (slug) => {
    const buttonTexts = {
      "book-writing": "Write Your Book",
      "book-publishing": "Publish Your Book",
      "book-marketing": "Promote Your Book",
      "audiobook-production": "Create Audiobook",
      "book-editing": "Perfect Your Book",
      "book-printing": "Print Your Book"
    };
    return buttonTexts[slug] || "Get Started";
  };

  const getFormDescription = (slug) => {
    const descriptions = {
      "book-writing": "Transform your vision into a bestselling manuscript with world-class ghostwriters who bring stories to life.",
      "book-publishing": "Get published on Amazon, Barnes & Noble, and 40K+ retailers-from ISBN to global distribution.",
      "book-marketing": "Data-driven campaigns that skyrocket sales, build your brand, and turn readers into raving fans.",
      "audiobook-production": "Studio-quality narration that captivates listeners on Audible, iTunes, and beyond.",
      "book-editing": "Publisher-grade editing that transforms good writing into exceptional, market-ready manuscripts.",
      "book-printing": "Premium print quality with flexible options-from single copies to bulk orders delivered fast."
    };
    return descriptions[slug] || "Fill out the form to get your free consultation and custom quote.";
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(contactForm);
    
    // Reset form only on success
    if (formStatus.type === "success") {
      setContactForm({ 
        name: "", 
        email: "", 
        phone: "", 
        countryCode: "+1",
        message: ""
      });
    }
  };

  const serviceTestimonials = testimonials.filter(
    t => t.service === service.slug || t.service === "general"
  ).slice(0, 4);

  const relatedServices = services.filter(s => s.id !== service.id).slice(0, 6);
  
  const genres = ["Children\'s Book", "Fiction", "Memoir", "Non-Fiction", "Poetry"];
  const filteredProjects = portfolioProjects.filter(project => project.genre === activeGenre);

  return (
    <div className="min-h-screen bg-[#000000]">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="hero-background">
          <img
            src={service.hero.backgroundImage || "https://images.unsplash.com/photo-1457369804613-52c61a468e7d?w=1920"}
            alt={`${service.title} Background`}
            className="hero-image"
          />
          <div className="hero-overlay-split"></div>
        </div>
        
        {/* Animated Background Icons */}
        <AnimatedIcon icon="sparkles" position="top-right" size={100} delay={0.5} opacity={0.06} />
        <AnimatedIcon icon="trending" position="bottom-left" size={90} delay={2} opacity={0.07} />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div data-aos="fade-right">
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-6 leading-tight">
                {service.hero.title}
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                {service.hero.subtitle}
              </p>
              
              <div className="space-y-4 mb-8">
                {service.hero.highlights.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-[#F4D03F] flex-shrink-0 mt-1" />
                    <p className="text-gray-300">{highlight}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="btn-primary"
                  onClick={() => openServicePopup(getButtonText(service.slug), `Get started with our professional ${service.title.toLowerCase()} services. Fill out the form to receive a free consultation and custom quote.`)}
                >
                  {getButtonText(service.slug)} <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button 
                  className="js-chat btn-secondary"
                  onClick={openChat}
                >
                  Discuss Your Project
                </Button>
              </div>
            </div>

            {/* Right Side - Enhanced Contact Form (Home Page Design) */}
            <div className="hero-form-container" data-aos="fade-left">
              <Card className="bg-gradient-to-br from-[#2C3E50] to-[#1a1a1a] border-2 border-[#F4D03F] shadow-2xl backdrop-blur-sm relative overflow-hidden">
                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-16 sm:w-24 h-16 sm:h-24 bg-[#F4D03F] opacity-10 rounded-bl-full"></div>
                <div className="absolute bottom-0 left-0 w-12 sm:w-20 h-12 sm:h-20 bg-[#F8D26B] opacity-10 rounded-tr-full"></div>
                
                <CardHeader className="relative z-10 p-3 sm:p-5">
                  <CardTitle className="text-[#F4D03F] text-2xl sm:text-3xl font-bold uppercase text-center mb-2">
                    {getButtonText(service.slug)}
                  </CardTitle>
                  <CardDescription className="text-[#ffffff] text-center" style={{ fontSize: "1.25rem" }}>
                    {getFormDescription(service.slug)}
                  </CardDescription>
                </CardHeader>
                <CardContent className="relative z-10 p-3 sm:p-5">
                  <form onSubmit={handleContactSubmit} className="space-y-2 sm:space-y-3">
                    <div>
                      <Input
                        type="text"
                        placeholder="Enter your full name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        aria-label="Your Name"
                        className="bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] h-10 sm:h-12 text-sm sm:text-base rounded-lg transition-all duration-300 hover:border-[#F8D26B]"
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        placeholder="your.email@example.com"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        aria-label="Your Email"
                        className="bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] h-10 sm:h-12 text-sm sm:text-base rounded-lg transition-all duration-300 hover:border-[#F8D26B]"
                      />
                    </div>
                    {/* Country Display from Geolocation */}
                    {geoData?.isSuccess && geoData?.data?.countryName && (
                      <div className="bg-[#2C3E50]/30 border-2 border-[#F4D03F]/30 rounded-lg p-3 flex items-center gap-2 hidden">
                        <MapPin className="text-[#F4D03F]" size={18} />
                        <span className="text-[#ffffff] text-sm">
                          <span className="text-[#F4D03F] font-semibold">{geoData.data.countryName}</span>
                        </span>
                      </div>
                    )}
                    <div>
                      <div className="flex gap-2">
                        <select
                          value={contactForm.countryCode}
                          onChange={(e) => setContactForm({...contactForm, countryCode: e.target.value})}
                          aria-label="Country Code"
                          className="bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] rounded-lg px-2 py-2 text-sm sm:text-base focus:outline-none focus:border-[#F4D03F] transition-all duration-300 hover:border-[#F8D26B]"
                          style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
                        >
                          {countryCodes.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.country} {country.code}
                            </option>
                          ))}
                        </select>
                        <Input
                          type="tel"
                          placeholder="(123) 456-7890"
                          value={contactForm.phone}
                          onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                          required
                          aria-label="Your Phone"
                          className="flex-1 bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] h-10 sm:h-12 text-sm sm:text-base rounded-lg transition-all duration-300 hover:border-[#F8D26B]"
                        />
                      </div>
                    </div>
                    <div>
                      <Textarea
                        placeholder="Tell us about your book project..."
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        rows={4}
                        aria-label="Your Message"
                        className="bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] text-lg rounded-lg transition-all duration-300 hover:border-[#F8D26B]"
                      />
                    </div>
                    <Button type="submit" className="btn-primary w-full h-10 sm:h-12 text-sm sm:text-base font-bold shadow-lg hover:shadow-2xl transition-all duration-300">
                      Start Your Project →
                    </Button>
                    
                    {/* Enhanced Tagline with Divider */}
                    <div className="mt-4 flex flex-col items-center">
                      {/* Thin Divider Above */}
                      <div className="w-[40%] h-[1px] bg-white/8 mb-3"></div>
                      
                      {/* Enhanced Tagline with Shimmer */}
                      <p 
                        className="text-center font-semibold shimmer-text"
                        style={{
                          fontSize: "1.25rem",
                          color: "#E0BF5A",
                          opacity: 0.95,
                          textShadow: "0 1px 2px rgba(0, 0, 0, 0.6)",
                          letterSpacing: "0.2px"
                        }}
                      >
                        ✓ Free consultation • ✓ No obligation • ✓ Expert guidance
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section with Image */}
      <section className="py-16 sm:py-20 bg-[#000000] relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Side - Content */}
            <div data-aos="fade-right">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#F4D03F] mb-6">
                {service.title === "Book Writing" && "Transform Your Ideas Into Published Reality"}
                {service.title === "Book Publishing" && "End-to-End Publishing Excellence"}
                {service.title === "Book Marketing" && "Strategic Marketing That Drives Sales"}
                {service.title === "Audiobook Production" && "Professional Audio Excellence"}
                {service.title === "Book Editing" && "Polish Your Manuscript to Perfection"}
                {service.title === "Book Printing" && "Premium Quality Print on Demand"}
              </h2>
              
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                {service.description}
              </p>

              {/* Bullet Points */}
              <div className="space-y-4 mb-8">
                {service.title === "Book Writing" && (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Professional ghostwriters with proven publishing track records</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Unlimited revisions until you're 100% satisfied</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Full copyright ownership and publishing rights</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Collaborative process with regular updates</p>
                    </div>
                  </>
                )}
                
                {service.title === "Book Publishing" && (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Distribution to Amazon, Barnes & Noble, and 40,000+ retailers</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">ISBN registration and copyright filing included</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Both digital and print-on-demand options available</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Retain 100% of your royalties and rights</p>
                    </div>
                  </>
                )}
                
                {service.title === "Book Marketing" && (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Targeted social media campaigns that reach your ideal readers</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Amazon optimization for maximum visibility and sales</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Email marketing campaigns to build your author platform</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Book launch strategy with press releases and reviews</p>
                    </div>
                  </>
                )}
                
                {service.title === "Audiobook Production" && (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Professional voice actors with extensive audiobook experience</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Studio-quality recording and post-production</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Distribution to Audible, iTunes, and all major platforms</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Audition samples to choose your perfect narrator</p>
                    </div>
                  </>
                )}
                
                {service.title === "Book Editing" && (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Comprehensive editing: developmental, line, and copy editing</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Experienced editors specialized in your genre</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Detailed feedback with tracked changes and comments</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Fast turnaround without compromising quality</p>
                    </div>
                  </>
                )}
                
                {service.title === "Book Printing" && (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Premium paper quality and professional binding options</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Print-on-demand with no minimum order requirements</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Multiple format options: paperback, hardcover, large print</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">Fast shipping and worldwide delivery available</p>
                    </div>
                  </>
                )}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="btn-primary"
                  onClick={() => openServicePopup(getButtonText(service.slug), `Get started with our professional ${service.title.toLowerCase()} services. Fill out the form to receive a free consultation and custom quote.`)}
                >
                  {getButtonText(service.slug)}
                </Button>
                <Button 
                  className="btn-secondary"
                  onClick={openChat}
                >
                  Chat With Expert
                </Button>
              </div>
            </div>

            {/* Right Side - Image */}
            <div data-aos="fade-left" className="relative">
              <div className="relative rounded-lg overflow-hidden shadow-2xl border-2 border-[#F4D03F]/20 h-[500px] lg:h-[550px]">
                <img 
                  src={
                    service.title === "Book Writing" ? "https://images.unsplash.com/photo-1559454510-9faafe42764c?w=800" :
                    service.title === "Book Publishing" ? "https://images.unsplash.com/photo-1503694978374-8a2fa686963a?w=800" :
                    service.title === "Book Marketing" ? "https://images.unsplash.com/photo-1683721003111-070bcc053d8b?w=800" :
                    service.title === "Audiobook Production" ? "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800" :
                    service.title === "Book Editing" ? "https://images.unsplash.com/photo-1753299942901-b1feb2dc52f1?w=800" :
                    service.title === "Book Printing" ? "https://images.pexels.com/photos/13407343/pexels-photo-13407343.jpeg?w=800" :
                    "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800"
                  }
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#000000]/50 to-transparent"></div>
              </div>
              
              {/* Decorative element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#F4D03F] opacity-10 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process-section" className="py-20 bg-[#0a0a0a]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Our Proven Process
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              A streamlined approach that turns your vision into reality
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.processSteps.map((step, index) => (
              <div 
                key={index}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="relative"
              >
                <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-[#2C3E50] hover:border-[#F4D03F] transition-all duration-300 h-full">
                  <CardContent className="p-8">
                    <div className="text-6xl font-bold text-[#F4D03F] mb-4 opacity-20">
                      {step.number}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">
                      {step.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
                {index < service.processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-8 h-8 text-[#F4D03F]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-16 sm:py-20 md:py-24 bg-[#000000]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#F4D03F] mb-4 uppercase">OUR PORTFOLIO</h2>
            <p className="text-base sm:text-lg text-[#ffffff] max-w-2xl mx-auto">
              Browse our collection of published books across different genres. Each book represents our dedication to quality publishing.
            </p>
          </div>

          {/* Genre Tabs */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12" data-aos="fade-up" data-aos-delay="100">
            {genres.map((genre) => (
              <button
                key={genre}
                onClick={() => setActiveGenre(genre)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-xs sm:text-sm uppercase tracking-wide transition-all duration-300 ${
                  activeGenre === genre
                    ? "bg-[#F4D03F] text-[#000000] shadow-lg scale-105"
                    : "bg-[#2C3E50] text-[#ffffff] hover:bg-[#F8D26B] hover:text-[#000000]"
                }`}
              >
                {genre}
              </button>
            ))}
          </div>

          {/* Books Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                className="portfolio-book-card group"
                data-aos="zoom-in"
                data-aos-delay={index * 50}
              >
                <Card className="bg-[#2C3E50] border-[#F8D26B] border-opacity-30 overflow-hidden h-full hover:border-[#F4D03F] transition-all duration-500 hover:shadow-2xl hover:shadow-[#F4D03F]/20">
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-[#000000]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center p-4 sm:p-6">
                      <h3 className="text-[#F4D03F] font-bold text-base sm:text-xl mb-2 text-center">{project.title}</h3>
                      <p className="text-[#ffffff] text-xs sm:text-sm text-center">{project.description}</p>
                    </div>
                    {/* Genre Badge */}
                    <div className="absolute top-2 sm:top-4 left-2 sm:left-4 bg-[#F8D26B] text-[#000000] px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold uppercase">
                      {project.genre}
                    </div>
                  </div>
                  <CardContent className="p-3 sm:p-6">
                    <h3 className="text-[#ffffff] font-bold text-sm sm:text-lg mb-2 group-hover:text-[#F4D03F] transition-colors truncate">
                      {project.title}
                    </h3>
                    <div className="flex justify-between items-center text-xs sm:text-sm text-[#F4D03F]">
                      <span className="flex items-center gap-1 truncate">
                        <Check size={12} className="text-[#F8D26B] flex-shrink-0" />
                        <span className="truncate">{project.author}</span>
                      </span>
                      <span className="flex-shrink-0 bg-[#F4D03F]/20 text-[#F4D03F] px-2 py-0.5 rounded text-xs font-semibold">
                        {project.status || "Published"}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {/* No Results Message */}
          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-[#F4D03F] text-base sm:text-lg">No books found in this genre yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Services Section - Full Version from Home */}
      <section id="services" className="py-24 bg-[#302f2c]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">OUR SERVICES</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Comprehensive publishing solutions tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((serviceItem, index) => {
              const IconComponent = iconMap[serviceItem.icon];
              return (
                <Card 
                  key={serviceItem.id} 
                  className="bg-[#000000] border-[#3f4816] hover:border-[#F4D03F] transition-all duration-300 hover:transform hover:scale-105 card-animate flex flex-col h-[500px] overflow-hidden"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <CardHeader className="flex-shrink-0">
                    <div 
                      className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-4 service-icon-animated"
                      style={{ animationDelay: `${index * 0.2}s` }}
                    >
                      {serviceItem.gifUrl ? (
                        <img 
                          src={serviceItem.gifUrl} 
                          alt={serviceItem.title}
                          className="w-10 h-10 object-contain"
                        />
                      ) : (
                        <IconComponent className="text-[#000000]" size={28} />
                      )}
                    </div>
                    <CardTitle className="text-[#F4D03F] text-xl font-bold mb-2">
                      {serviceItem.title}
                    </CardTitle>
                    <p className="text-[#888680] text-base leading-snug">
                      {serviceItem.description}
                    </p>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col pt-4 overflow-hidden min-h-0">
                    <ul className="space-y-2 overflow-y-auto scrollbar-thin pr-2 flex-shrink mb-4">
                      {serviceItem.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-[#dfddd6] text-sm">
                          <Check size={16} className="text-[#F4D03F] flex-shrink-0 mt-1" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto flex flex-col gap-2 flex-shrink-0">
                      <a href={`/services/${serviceItem.slug}`} className="w-full">
                        <Button className="bg-[#F4D03F] text-black hover:bg-[#F8D26B] font-bold w-full">
                          Learn More
                        </Button>
                      </a>
                      <Button 
                        onClick={openChat}
                        className="js-chat btn-secondary w-full"
                      >
                        Discuss Your Project
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-[#000000]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose Us
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Experience the advantages of working with industry professionals
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.benefits.map((benefit, index) => {
              const IconComponent = iconMap[benefit.icon];
              return (
                <div
                  key={index}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                  className="text-center"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-[#F4D03F] to-[#C89A16] rounded-full flex items-center justify-center mx-auto mb-6">
                    {IconComponent && <IconComponent className="w-10 h-10 text-black" />}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-400">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1516979187457-637abb4f9353?w=1920')] bg-cover bg-center"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center" data-aos="fade-up">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Take the first step toward publishing success. Join the league of bestselling authors today.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <Button 
                className="bg-[#F4D03F] text-black hover:bg-[#F8D26B] font-bold px-8 py-6 text-lg"
                onClick={() => openServicePopup("Get Your Free Quote", `Ready to bring your book to life? Fill out the form below and our expert team will get back to you within 24 hours with a custom quote tailored to your project.`)}
              >
                Get a Free Quote
              </Button>
              <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-3 text-[#F4D03F] hover:text-[#F8D26B] transition-colors">
                <div className="w-12 h-12 bg-[#F4D03F] rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6 text-black" />
                </div>
                <span className="text-xl font-semibold">{companyInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Slider Version from Home */}
      <section className="py-24 bg-[#302f2c]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">CLIENT SUCCESS STORIES</h2>
            <p className="text-xl text-gray-400">Success stories from our published authors</p>
          </div>

          {/* Testimonials Slider */}
          <div className="relative mb-12">
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentTestimonialIndex * 100}%)` }}
              >
                {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, slideIndex) => (
                  <div key={slideIndex} className="min-w-full">
                    <div className="grid md:grid-cols-3 gap-8 px-4">
                      {testimonials.slice(slideIndex * 3, slideIndex * 3 + 3).map((testimonial) => (
                        <Card key={testimonial.id} className="bg-[#000000] border-[#3f4816]">
                          <CardContent className="pt-6">
                            <div className="flex gap-1 mb-4">
                              {[...Array(testimonial.rating)].map((_, i) => (
                                <Star key={i} size={20} className="fill-[#F4D03F] text-[#F4D03F]" />
                              ))}
                            </div>
                            <p className="text-[#dfddd6] mb-6 italic">"{testimonial.content}"</p>
                            <div>
                              <div className="text-[#F4D03F] font-bold">{testimonial.author}</div>
                              <div className="text-[#888680] text-sm">{testimonial.role}</div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Slider Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonialIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentTestimonialIndex === index 
                      ? "bg-[#F4D03F] w-8" 
                      : "bg-[#888680] hover:bg-[#F8D26B]"
                  }`}
                  aria-label={`Go to testimonial slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="text-center">
            <p className="text-[#dfddd6] mb-4">Have you worked with us? Share your experience!</p>
            <Button 
              className="bg-[#F4D03F] text-black hover:bg-[#F8D26B] font-bold px-8 py-4"
              onClick={() => document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" })}
            >
              Submit Your Feedback
            </Button>
          </div>
        </div>
      </section>

      {/* Final Contact Form Section */}
      <section id="contact-form" className="py-20 bg-[#0a0a0a]">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <Card className="bg-[#1a1a1a] border-[#2C3E50] shadow-2xl" data-aos="fade-up">
              <CardHeader className="bg-gradient-to-r from-[#F4D03F] to-[#C89A16] text-black">
                <CardTitle className="text-3xl text-center">
                  Let's Bring Your Book to Life
                </CardTitle>
                <p className="text-center text-black/80 mt-2">
                  Fill out the form and our team will contact you within 24 hours
                </p>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div>
                    <Input
                      placeholder="Your Name *"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      required
                      className="bg-[#2C3E50] border-[#F4D03F] text-white placeholder:text-gray-400"
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      placeholder="Your Email *"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      required
                      className="bg-[#2C3E50] border-[#F4D03F] text-white placeholder:text-gray-400"
                    />
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={contactForm.countryCode}
                      onChange={(e) => setContactForm({...contactForm, countryCode: e.target.value})}
                      className="bg-[#2C3E50] border border-[#F4D03F] text-white rounded-md px-2 py-2 focus:outline-none focus:border-[#F4D03F]"
                      style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
                    >
                      {countryCodes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.country} {country.code}
                        </option>
                      ))}
                    </select>
                    <Input
                      type="tel"
                      placeholder="Your Phone *"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                      required
                      className="flex-1 bg-[#2C3E50] border-[#F4D03F] text-white placeholder:text-gray-400"
                    />
                  </div>
                  <div>
                    <Textarea
                      placeholder="Tell us about your project *"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      required
                      rows={6}
                      className="bg-[#2C3E50] border-[#F4D03F] text-white placeholder:text-gray-400"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#F4D03F] text-black hover:bg-[#F8D26B] font-bold py-6 text-lg"
                  >
                    Submit Your Request
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Popup */}
      <ServicePopup 
        isOpen={popupOpen}
        onClose={() => setPopupOpen(false)}
        service={popupService}
      />
    </div>
  );
};

export default ServicePage;
